import { above, below } from 'utils/mediaqueries';

import PropTypes from 'prop-types';
import React from 'react';
import moduleMargins from 'config/theme/moduleMargins';
import styled from 'libs/styled';

/* 
    Note: The special two columns purpose is to always align the module-body of the
    child-column. For this to work it's neccesary that the content-block used as a
    column has the following structure:

    <div className="module-wrapper">
        <div className="module-heading">
            Content display above main content
        </div>
        <div className="module-body">
            Main Content
        </div>
        <div className="module-footer">
            Content display below main content
        </div>
    </div>
    
    This will allow the SpecialTwoColumnsLayout to achive a layout similar to below:
     _____ 
    |_____|  _____
    |     | |     |
    |_____| |_____|
            |_____|
*/

const Row = styled('div')`
    margin-left: -8px;
    margin-right: -8px;

    @supports (display: grid) {
        ${above.sm} {
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: auto auto auto;
        }
    }

    @supports not (display: grid) {
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
    }

    ${above.md} {
        margin-left: -12px;
        margin-right: -12px;
    }
`;

const Col = styled('div', {
    shouldForwardProp: prop => ['columnMarginsTop', 'columnMarginsBottom'].indexOf(prop) === -1,
})`
    padding: 0px 8px;

    ${above.md} {
        padding: 0px 12px;
    }

    @supports (display: grid) {
        display: contents;

        .module-wrapper {
            display: contents;
        }

        .module-heading,
        .module-body,
        .module-footer {
            padding: 0px 8px;
        }

        ${above.md} {
            .module-heading,
            .module-body,
            .module-footer {
                padding: 0px 12px;
            }
        }

        .module-heading {
            display: flex;
            justify-content: flex-end;
            grid-row-start: 1;
            grid-row-end: 2;
        }

        .module-body {
            grid-row-start: 2;
            grid-row-end: 3;
        }

        .module-footer {
            grid-row-start: 3;
            grid-row-end: end;
        }

        // RowMargins is used instead of columnMargins above sm (when the columns are horizontal instead of vertical)
        &:first-of-type {
            .module-footer {
                ${({ columnMarginsBottom }) =>
                    columnMarginsBottom[0] ? `${below.xs}{ margin-bottom: ${columnMarginsBottom[0]}}` : null}

                ${({ columnMarginsBottom }) =>
                    columnMarginsBottom[1] ? `${below.sm}{ margin-bottom: ${columnMarginsBottom[1]}}` : null}
            }
        }
        &:last-of-type {
            .module-header {
                ${({ columnMarginsTop }) =>
                    columnMarginsTop[0] ? `${below.xs}{ margin-top: ${columnMarginsTop[0]}}` : null}

                ${({ columnMarginsTop }) =>
                    columnMarginsTop[1] ? `${below.sm}{ margin-top: ${columnMarginsTop[1]}}` : null}
            }
        }
    }

    @supports not (display: grid) {
        max-width: 100%;
        flex: 0 0 100%;

        ${above.sm} {
            max-width: 50%;
            flex: 0 0 50%;
        }
    }
`;

const ColumnsLayout = ({ blocks = [] }) => {
    if (blocks.length < 1) {
        return null;
    }
    // Custom moduleMargin
    const moduleMarginsNone = [null, null];

    /*
        Calculates the margins for columns and rows depending on what blocks are inside.
    */
    const { colMargins, rowMargins } = blocks.reduce(
        (prevComponent, Component) => {
            let colMargins = {
                top: moduleMarginsNone,
                bottom: moduleMarginsNone,
            };

            switch (Component.props.data.layout) {
                case 'call_to_action':
                    colMargins = {
                        top: moduleMargins.md,
                        bottom: moduleMargins.md,
                    };
                    break;
                case 'call_to_action_basic':
                    colMargins = {
                        top: moduleMargins.md,
                        bottom: moduleMarginsNone,
                    };
                    break;
                case 'image' || 'video_inline':
                    colMargins = {
                        top:
                            Component.props.data.settings?.indexOf('link') !== -1
                                ? moduleMargins.xs
                                : moduleMarginsNone,
                        bottom: moduleMarginsNone,
                    };
                    break;
                default:
            }

            // Use the longest array for the loop to go through all breakpoints
            const longestMarginsArray = Math.max(
                colMargins.top.length,
                colMargins.bottom.length,
                prevComponent.colMargins.top.length,
                prevComponent.colMargins.bottom.length
            );

            // Go through all the breakpoints and pick out the highest value
            // This is used to put margin top and bottom on the row
            const rowMargins = [...new Array(longestMarginsArray)].reduce(
                (acc, _, index) => {
                    // Convert strings to numbers to be able to compare
                    const currentMarginTopInt = parseInt(colMargins.top[index], 10);
                    const currentMarginBottomInt = parseInt(colMargins.bottom[index], 10);

                    // Save the highest number or pick previous number if value is missing
                    acc.top.push(
                        currentMarginTopInt > prevComponent.rowMargins.top[index] ||
                            (prevComponent.rowMargins.top[index] === null && !isNaN(currentMarginTopInt))
                            ? currentMarginTopInt
                            : prevComponent.rowMargins.top[index]
                    );
                    acc.bottom.push(
                        currentMarginBottomInt > prevComponent.rowMargins.bottom[index] ||
                            (prevComponent.rowMargins.bottom[index] === null && !isNaN(currentMarginBottomInt))
                            ? currentMarginBottomInt
                            : prevComponent.rowMargins.bottom[index]
                    );

                    return acc;
                },
                {
                    top: [],
                    bottom: [],
                }
            );

            return {
                colMargins: {
                    top: [...prevComponent.colMargins.top, colMargins.top],
                    bottom: [...prevComponent.colMargins.bottom, colMargins.bottom],
                },
                rowMargins,
            };
        },
        {
            colMargins: { top: moduleMarginsNone, bottom: moduleMarginsNone },
            rowMargins: { top: moduleMarginsNone, bottom: moduleMarginsNone },
        }
    );

    // Remove undefiends and put px back on breakpoints with value
    const transformedRowMarginsTop = rowMargins.top.map(value =>
        value !== null && value !== undefined ? `${value}px` : null
    );
    const transformedRowMarginsBottom = rowMargins.bottom.map(value =>
        (value !== null && value !== undefined) !== null ? `${value}px` : null
    );

    return (
        <Row mt={transformedRowMarginsTop} mb={transformedRowMarginsBottom}>
            {blocks.map((Component, index) => {
                if (!Component) {
                    return null;
                }

                return (
                    <Col
                        columnMarginsTop={colMargins.top[index] || []}
                        columnMarginsBottom={colMargins.bottom[index] || []}
                        key={`${index}-${Component.key}`}
                    >
                        {Component}
                    </Col>
                );
            })}
        </Row>
    );
};

ColumnsLayout.propTypes = {
    blocks: PropTypes.arrayOf(PropTypes.node),
};

export default ColumnsLayout;
